/**
 * Newsletter Topics Admin Page
 *
 * Two tabs: Topics (stats, filters, table) and Settings (space config, Google Chat auth).
 */
import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  Clear as ClearIcon,
  SettingsOutlined as SettingsIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  NewspaperOutlined as TopicsIcon,
} from '@mui/icons-material';
import { newsletterApi } from '@/services/api';
import { GoogleChatSetup } from '@/components/integrations';
import { useAIOrbitModels } from '@/hooks/useAIOrbitModels';
import type { NewsletterTopic } from '@/types';

const categoryOptions = [
  { value: '', label: 'All Categories' },
  { value: 'success_story', label: 'Success Stories' },
  { value: 'recognition', label: 'Recognition' },
  { value: 'open_source', label: 'Open Source Contributions' },
  { value: 'industry_pulse', label: 'Industry Pulse' },
  { value: 'other', label: 'Other' },
];

const categoryColors: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'info' | 'warning'> = {
  success_story: 'success',
  recognition: 'primary',
  open_source: 'info',
  industry_pulse: 'warning',
  other: 'default',
};

interface ModelOption {
  label: string;
  id: string;
  group: string;
  tier?: string;
}

// Well-known models grouped by provider
const WELL_KNOWN_MODELS: ModelOption[] = [
  // IONOS AI Model Hub — https://docs.ionos.com/cloud/ai/ai-model-hub/models
  { label: 'Llama 3.1 8B Instruct', id: 'meta-llama/Meta-Llama-3.1-8B-Instruct', group: 'IONOS AI Model Hub', tier: 'standard' },
  { label: 'Teuken 7B Instruct', id: 'openGPT-X/Teuken-7B-instruct-commercial', group: 'IONOS AI Model Hub', tier: 'standard' },
  { label: 'Mistral Nemo Instruct', id: 'mistralai/Mistral-Nemo-Instruct-2407', group: 'IONOS AI Model Hub', tier: 'standard' },
  { label: 'Code Llama 13B Instruct', id: 'meta-llama/CodeLlama-13b-Instruct-hf', group: 'IONOS AI Model Hub', tier: 'plus' },
  { label: 'Mistral Small 24B Instruct', id: 'mistralai/Mistral-Small-24B-Instruct', group: 'IONOS AI Model Hub', tier: 'plus' },
  { label: 'GPT-OSS 120B', id: 'openai/gpt-oss-120b', group: 'IONOS AI Model Hub', tier: 'premium' },
  { label: 'Llama 3.3 70B Instruct', id: 'meta-llama/Llama-3.3-70B-Instruct', group: 'IONOS AI Model Hub', tier: 'premium' },
  { label: 'Llama 3.1 405B Instruct', id: 'meta-llama/Meta-Llama-3.1-405B-Instruct-FP8', group: 'IONOS AI Model Hub', tier: 'premium' },
  // Anthropic Claude — latest
  { label: 'Claude Opus 4.6', id: 'claude-opus-4-6', group: 'Anthropic Claude' },
  { label: 'Claude Sonnet 4.6', id: 'claude-sonnet-4-6', group: 'Anthropic Claude' },
  { label: 'Claude Haiku 4.5', id: 'claude-haiku-4-5-20251001', group: 'Anthropic Claude' },
  // Anthropic Claude — previous generation
  { label: 'Claude Sonnet 4.5', id: 'claude-sonnet-4-5-20250929', group: 'Anthropic Claude' },
  { label: 'Claude Opus 4.5', id: 'claude-opus-4-5-20251101', group: 'Anthropic Claude' },
  { label: 'Claude Sonnet 4', id: 'claude-sonnet-4-20250514', group: 'Anthropic Claude' },
  { label: 'Claude Opus 4', id: 'claude-opus-4-20250514', group: 'Anthropic Claude' },
  // OpenAI
  { label: 'GPT-4o', id: 'gpt-4o', group: 'OpenAI' },
  { label: 'GPT-4o Mini', id: 'gpt-4o-mini', group: 'OpenAI' },
  { label: 'GPT-4 Turbo', id: 'gpt-4-turbo', group: 'OpenAI' },
  { label: 'GPT-3.5 Turbo', id: 'gpt-3.5-turbo', group: 'OpenAI' },
];

export default function NewsletterTopicsPage() {
  const queryClient = useQueryClient();
  const { options: aiorbitModels } = useAIOrbitModels();
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [category, setCategory] = useState('');
  const [authorSearch, setAuthorSearch] = useState('');
  const [search, setSearch] = useState('');
  const [selectedTopic, setSelectedTopic] = useState<NewsletterTopic | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [topicToDelete, setTopicToDelete] = useState<NewsletterTopic | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [newsletterSpaceId, setNewsletterSpaceId] = useState('');
  const [pollingEnabled, setPollingEnabled] = useState(true);
  const [aiProviderType, setAiProviderType] = useState<string | null>(null);
  const [aiApiEndpoint, setAiApiEndpoint] = useState('');
  const [aiApiKey, setAiApiKey] = useState('');
  const [aiModelOverride, setAiModelOverride] = useState('');
  const [modelInputValue, setModelInputValue] = useState('');
  const [hasApiKey, setHasApiKey] = useState(false);
  const [testingAI, setTestingAI] = useState(false);
  const [aiTestResult, setAiTestResult] = useState<{ status: string; message: string } | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch topics with filters
  const { data, isLoading, error } = useQuery({
    queryKey: ['newsletter-topics', fromDate, toDate, category, authorSearch, search, page, rowsPerPage],
    queryFn: () =>
      newsletterApi
        .list({
          from_date: fromDate || undefined,
          to_date: toDate || undefined,
          category: category || undefined,
          author: authorSearch || undefined,
          search: search || undefined,
          limit: rowsPerPage,
          offset: page * rowsPerPage,
        })
        .then((res) => res.data),
    placeholderData: (previousData) => previousData,
  });

  // Fetch category stats
  const { data: categoryStats } = useQuery({
    queryKey: ['newsletter-categories'],
    queryFn: () => newsletterApi.getCategoryStats().then((res) => res.data),
  });

  // Fetch newsletter settings
  const { data: settings } = useQuery({
    queryKey: ['newsletter-settings'],
    queryFn: () => newsletterApi.getSettings().then((res) => res.data),
  });

  // Initialize settings state when data is loaded
  useEffect(() => {
    if (settings) {
      setNewsletterSpaceId(settings.newsletter_space_id || '');
      setPollingEnabled(settings.enable_newsletter_polling);
      setAiProviderType(settings.ai_provider_type || null);
      setAiApiEndpoint(settings.ai_api_endpoint || '');
      setAiModelOverride(settings.ai_model_override || '');
      setModelInputValue(settings.ai_model_override || '');
      setHasApiKey(settings.has_api_key || false);
    }
  }, [settings]);

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: (updates: Record<string, unknown>) =>
      newsletterApi.updateSettings(updates as Parameters<typeof newsletterApi.updateSettings>[0]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsletter-settings'] });
      setSnackbar({ open: true, message: 'Settings saved successfully!', severity: 'success' });
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: `Failed to save settings: ${error.message}`, severity: 'error' });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (topicId: string) => newsletterApi.delete(topicId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsletter-topics'] });
      queryClient.invalidateQueries({ queryKey: ['newsletter-categories'] });
      setDeleteDialogOpen(false);
      setTopicToDelete(null);
      setSnackbar({ open: true, message: 'Topic deleted successfully!', severity: 'success' });
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: `Failed to delete: ${error.message}`, severity: 'error' });
    },
  });

  const handleClearFilters = () => {
    setFromDate('');
    setToDate('');
    setCategory('');
    setAuthorSearch('');
    setSearch('');
    setPage(0);
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['newsletter-topics'] });
    queryClient.invalidateQueries({ queryKey: ['newsletter-categories'] });
    setSnackbar({ open: true, message: 'Refreshed topics list', severity: 'success' });
  };

  const handleExport = async () => {
    if (!fromDate || !toDate) {
      setSnackbar({ open: true, message: 'Please select both from and to dates for export', severity: 'error' });
      return;
    }

    try {
      // Convert YYYY-MM-DD to ISO format with time
      const fromDateISO = `${fromDate}T00:00:00Z`;
      const toDateISO = `${toDate}T23:59:59Z`;

      const response = await newsletterApi.exportMarkdown(fromDateISO, toDateISO, category || undefined);
      const blob = new Blob([response.data], { type: 'text/markdown' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `newsletter-topics-${fromDate}-${toDate}.md`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSnackbar({ open: true, message: 'Export successful!', severity: 'success' });
    } catch (error) {
      console.error('Export error:', error);
      const message = error instanceof Error ? error.message : 'Failed to export topics';
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  const handleViewTopic = (topic: NewsletterTopic) => {
    setSelectedTopic(topic);
    setViewDialogOpen(true);
  };

  const handleDeleteClick = (topic: NewsletterTopic) => {
    setTopicToDelete(topic);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (topicToDelete) {
      deleteMutation.mutate(topicToDelete.id);
    }
  };

  const modelOptions = useMemo<ModelOption[]>(() => {
    // No provider override = platform default = AIOrbit (live model list).
    if (!aiProviderType) {
      return aiorbitModels.map((m) => ({ label: m.label, id: m.id, group: 'AIOrbit' }));
    }
    const visibleGroups =
      aiProviderType === 'anthropic'
        ? ['Anthropic Claude']
        : ['IONOS AI Model Hub', 'OpenAI'];
    return WELL_KNOWN_MODELS.filter((m) => visibleGroups.includes(m.group));
  }, [aiProviderType, aiorbitModels]);

  const handleSaveSettings = () => {
    const updates: Record<string, unknown> = {
      newsletter_space_id: newsletterSpaceId,
      enable_newsletter_polling: pollingEnabled,
      ai_provider_type: aiProviderType,
      ai_api_endpoint: aiProviderType === 'openai_compatible' ? aiApiEndpoint : null,
      ai_model_override: aiModelOverride || null,
    };
    if (aiApiKey) {
      updates.ai_api_key = aiApiKey;
    }
    updateSettingsMutation.mutate(updates);
  };

  const handleTestAI = async () => {
    setTestingAI(true);
    setAiTestResult(null);
    try {
      const res = await newsletterApi.testAIConnection({
        provider_type: aiProviderType,
        api_endpoint: aiProviderType === 'openai_compatible' ? aiApiEndpoint : null,
        api_key: aiApiKey || undefined,
        model_override: aiModelOverride || undefined,
      });
      setAiTestResult(res.data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Test failed';
      setAiTestResult({ status: 'error', message });
    } finally {
      setTestingAI(false);
    }
  };

  const handleResetAI = () => {
    setAiProviderType(null);
    setAiApiEndpoint('');
    setAiApiKey('');
    setAiModelOverride('');
    setModelInputValue('');
    setAiTestResult(null);
    updateSettingsMutation.mutate({
      newsletter_space_id: newsletterSpaceId,
      enable_newsletter_polling: pollingEnabled,
      ai_provider_type: null,
      ai_api_endpoint: null,
      ai_api_key: null,
      ai_model_override: null,
    });
  };

  const formatCategoryLabel = (cat: string) => {
    return categoryOptions.find((opt) => opt.value === cat)?.label || cat;
  };

  return (
    <Box>
      {/* Tab bar */}
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab icon={<TopicsIcon />} iconPosition="start" label="Topics" />
        <Tab icon={<SettingsIcon />} iconPosition="start" label="Settings" />
      </Tabs>

      {/* ===== TAB 0: Topics ===== */}
      {activeTab === 0 && (
        <Box>
          {/* Stats */}
          {categoryStats && (
            <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-around', flexWrap: 'wrap', alignItems: 'center' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Total Topics</Typography>
                    <Typography variant="subtitle1" fontWeight={700}>{data?.total || 0}</Typography>
                  </Box>
                  {categoryStats.map((stat) => (
                    <Box key={stat.category} sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                      <Typography variant="subtitle1" fontWeight={700}>{stat.count}</Typography>
                    </Box>
                  ))}
                </Box>
                <Tooltip title="Refresh">
                  <IconButton size="small" onClick={handleRefresh}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={filtersOpen ? 'Hide filters' : 'Show filters'}>
                  <IconButton size="small" onClick={() => setFiltersOpen(!filtersOpen)} color={filtersOpen ? 'primary' : 'default'}>
                    <FilterIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>
          )}

          {/* Filters (collapsible) */}
          <Collapse in={filtersOpen}>
            <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="From Date"
                    type="date"
                    value={fromDate}
                    onChange={(e) => {
                      setFromDate(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="To Date"
                    type="date"
                    value={toDate}
                    onChange={(e) => {
                      setToDate(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={category}
                      onChange={(e) => {
                        setCategory(e.target.value);
                        setPage(0);
                      }}
                      label="Category"
                    >
                      {categoryOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Search Author"
                    value={authorSearch}
                    onChange={(e) => {
                      setAuthorSearch(e.target.value);
                      setPage(0);
                    }}
                    size="small"
                    placeholder="Name or email"
                  />
                </Grid>
                <Grid item xs={12} md={9}>
                  <TextField
                    fullWidth
                    label="Search Content"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(0);
                    }}
                    size="small"
                    placeholder="Search in topic text"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<ClearIcon />}
                      onClick={handleClearFilters}
                      size="small"
                      sx={{ flex: 1 }}
                    >
                      Clear
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={handleExport}
                      size="small"
                      sx={{ flex: 1 }}
                      disabled={!fromDate || !toDate}
                    >
                      Export
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Collapse>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load topics: {error.message}
            </Alert>
          )}

          {/* Topics Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Author</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Topic Preview</TableCell>
                  <TableCell align="center">Links</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton width={100} /></TableCell>
                      <TableCell><Skeleton width={150} /></TableCell>
                      <TableCell><Skeleton width={120} /></TableCell>
                      <TableCell><Skeleton width={300} /></TableCell>
                      <TableCell><Skeleton width={40} /></TableCell>
                      <TableCell><Skeleton width={80} /></TableCell>
                    </TableRow>
                  ))
                ) : data?.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        No topics found. Adjust filters or wait for topics to be collected.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data?.items.map((topic) => (
                    <TableRow key={topic.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(topic.date).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {topic.author_name === 'Unknown' && topic.author_email
                            ? topic.author_email
                            : topic.author_name}
                        </Typography>
                        {topic.author_name !== 'Unknown' && topic.author_email && (
                          <Typography variant="caption" color="text.secondary">
                            {topic.author_email}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={formatCategoryLabel(topic.category)}
                          color={categoryColors[topic.category] || 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 400 }}>
                          {topic.confirmed_message.substring(0, 100)}
                          {topic.confirmed_message.length > 100 && '...'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={topic.links?.length || 0} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleViewTopic(topic)}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDeleteClick(topic)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={data?.total || 0}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </TableContainer>
        </Box>
      )}

      {/* ===== TAB 1: Settings ===== */}
      {activeTab === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Newsletter Space Configuration */}
          <Card variant="outlined">
            <CardHeader
              title="Newsletter Space"
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
              subheader="Configure the Google Chat space where newsletter topics are collected"
              subheaderTypographyProps={{ variant: 'body2' }}
              action={
                <Chip
                  label={pollingEnabled ? 'Active' : 'Disabled'}
                  size="small"
                  color={pollingEnabled ? 'success' : 'default'}
                  variant="outlined"
                />
              }
            />
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Space ID"
                    value={newsletterSpaceId}
                    onChange={(e) => setNewsletterSpaceId(e.target.value)}
                    placeholder="spaces/XXXXXX"
                    helperText="The Google Chat space ID where newsletter topics will be collected"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={pollingEnabled}
                        onChange={(e) => setPollingEnabled(e.target.checked)}
                      />
                    }
                    label="Enable Polling"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* AI Provider Configuration */}
          <Card variant="outlined">
            <CardHeader
              title="AI Provider"
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
              subheader="Configure the AI model used for newsletter summaries and categorization. By default the platform provider (AIOrbit) is used."
              subheaderTypographyProps={{ variant: 'body2' }}
              action={
                <Button
                  variant="outlined"
                  size="small"
                  color="warning"
                  onClick={handleResetAI}
                  disabled={!aiProviderType && !aiModelOverride}
                >
                  Reset to Platform Default
                </Button>
              }
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Provider Type</InputLabel>
                    <Select
                      value={aiProviderType || ''}
                      label="Provider Type"
                      onChange={(e) => {
                        const val = e.target.value || null;
                        setAiProviderType(val);
                        if (val === 'anthropic') {
                          setAiApiEndpoint('');
                        }
                        setAiTestResult(null);
                      }}
                    >
                      <MenuItem value="">
                        AIOrbit (platform default)
                      </MenuItem>
                      <MenuItem value="openai_compatible">OpenAI Compatible (IONOS / OpenAI)</MenuItem>
                      <MenuItem value="anthropic">Anthropic (Claude)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="API Key"
                    type="password"
                    value={aiApiKey}
                    onChange={(e) => setAiApiKey(e.target.value)}
                    disabled={!aiProviderType}
                    placeholder={hasApiKey ? '••••••••' : 'Enter API key'}
                    helperText={
                      hasApiKey
                        ? 'Stored. Enter new to replace.'
                        : aiProviderType
                          ? 'Optional, uses global key if empty'
                          : 'Not needed for the platform default'
                    }
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    freeSolo
                    selectOnFocus
                    clearOnBlur={false}
                    options={modelOptions}
                    groupBy={(option) => (typeof option === 'string' ? '' : option.group)}
                    getOptionLabel={(option) => (typeof option === 'string' ? option : option.id)}
                    inputValue={modelInputValue}
                    onInputChange={(_, value, reason) => {
                      if (reason === 'input' || reason === 'clear') {
                        setModelInputValue(value);
                        setAiModelOverride(value);
                      }
                    }}
                    onChange={(_, newValue) => {
                      if (newValue && typeof newValue !== 'string') {
                        setModelInputValue(newValue.id);
                        setAiModelOverride(newValue.id);
                      } else if (newValue === null) {
                        setModelInputValue('');
                        setAiModelOverride('');
                      }
                    }}
                    filterOptions={(options, { inputValue }) => {
                      const filter = inputValue.toLowerCase();
                      if (!filter) return options;
                      return options.filter(
                        (o) => o.id.toLowerCase().includes(filter) || o.label.toLowerCase().includes(filter)
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Model"
                        placeholder={aiProviderType ? 'e.g. meta-llama/Llama-3.3-70B-Instruct' : 'Platform default model'}
                        helperText="Select or type a model identifier"
                        size="small"
                      />
                    )}
                    renderOption={(props, option) => {
                      const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
                      return (
                        <li key={key} {...rest}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                            <Box>
                              <Typography variant="body2">{option.label}</Typography>
                              <Typography variant="caption" color="text.secondary">{option.id}</Typography>
                            </Box>
                            {option.tier && (
                              <Chip
                                label={option.tier}
                                size="small"
                                color={option.tier === 'premium' ? 'error' : option.tier === 'plus' ? 'warning' : 'default'}
                                sx={{ ml: 1, textTransform: 'capitalize' }}
                              />
                            )}
                          </Box>
                        </li>
                      );
                    }}
                    renderGroup={(params) => (
                      <li key={params.key}>
                        <Typography
                          variant="overline"
                          sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', color: 'text.secondary', fontWeight: 700 }}
                        >
                          {params.group}
                        </Typography>
                        <ul style={{ padding: 0 }}>{params.children}</ul>
                      </li>
                    )}
                    size="small"
                  />
                </Grid>
                {aiProviderType === 'openai_compatible' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="API Endpoint"
                      value={aiApiEndpoint}
                      onChange={(e) => setAiApiEndpoint(e.target.value)}
                      placeholder="https://openai.inference.de-txl.ionos.com/v1"
                      helperText="OpenAI-compatible API endpoint URL"
                      size="small"
                    />
                  </Grid>
                )}
              </Grid>
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleTestAI}
                  disabled={testingAI}
                >
                  {testingAI ? 'Testing...' : 'Test Connection'}
                </Button>
                {aiTestResult && (
                  <Alert severity={aiTestResult.status === 'success' ? 'success' : 'error'} sx={{ flex: 1 }}>
                    {aiTestResult.message}
                  </Alert>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Google Chat Authorization */}
          <Card variant="outlined">
            <CardHeader
              title="Google Chat Authorization"
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
              subheader="OAuth connection used to read messages from the newsletter space"
              subheaderTypographyProps={{ variant: 'body2' }}
            />
            <CardContent>
              <GoogleChatSetup />
            </CardContent>
          </Card>

          {/* Save All Settings */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<SaveIcon />}
              onClick={handleSaveSettings}
              disabled={updateSettingsMutation.isPending}
            >
              {updateSettingsMutation.isPending ? 'Saving...' : 'Save All Settings'}
            </Button>
          </Box>
        </Box>
      )}

      {/* View Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Newsletter Topic Details
          {selectedTopic && (
            <Chip
              label={formatCategoryLabel(selectedTopic.category)}
              color={categoryColors[selectedTopic.category] || 'default'}
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedTopic && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Author</Typography>
                  <Typography variant="body2">
                    {selectedTopic.author_name === 'Unknown' && selectedTopic.author_email
                      ? selectedTopic.author_email
                      : selectedTopic.author_name}
                  </Typography>
                  {selectedTopic.author_name !== 'Unknown' && selectedTopic.author_email && (
                    <Typography variant="caption" color="text.secondary">
                      {selectedTopic.author_email}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Date</Typography>
                  <Typography variant="body2">
                    {new Date(selectedTopic.date).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Original Message
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedTopic.original_message}
                  </Typography>
                </Paper>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Confirmed Message (Final)
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.50' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedTopic.confirmed_message}
                  </Typography>
                </Paper>
              </Box>

              {selectedTopic.links && selectedTopic.links.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Links
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {selectedTopic.links.map((link: string, index: number) => (
                      <Typography key={index} variant="body2">
                        <a href={link} target="_blank" rel="noopener noreferrer">
                          {link}
                        </a>
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Message ID: {selectedTopic.message_id}
                </Typography>
                <br />
                <Typography variant="caption" color="text.secondary">
                  Thread ID: {selectedTopic.thread_id}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this newsletter topic? This action cannot be undone.
          </Typography>
          {topicToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" noWrap>
                {topicToDelete.confirmed_message.substring(0, 100)}...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
